let currentLocale: string = "en-US";
const cache = new Map<string, Intl.NumberFormat>();

export function setLocale(locale: string) {
	cache.clear();
	currentLocale = locale ?? "en-US";
}
/**
 * `locale` overrides the one set at boot, for a caller that holds the
 * environment's own — see LocaleNumberInput, which parses against that locale
 * and so has to format against the same one. Omitted, nothing changes.
 *
 * It joins the cache key: two locales share an options object and would
 * otherwise share a formatter, so whichever asked first would win for both.
 */
function createFormatter(options?: Intl.NumberFormatOptions, locale?: string) {
	const key = `${locale ?? ""}|${JSON.stringify(options)}`;

	if (!cache.has(key)) {
		cache.set(key, new Intl.NumberFormat(locale ?? currentLocale, options));
	}
	return cache.get(key);
}

/**
 * Default
 */
export function defaultNumberFormatter(
	options?: Intl.NumberFormatOptions,
	locale?: string
) {
	return createFormatter({
		maximumFractionDigits: 2,
		...options,
	}, locale);
}

/**
 * Number without fractions
 */
export function numberNoFractionFormatter(
	options?: Intl.NumberFormatOptions,
	locale?: string
) {
	return createFormatter({
		maximumFractionDigits: 0,
		...options,
	}, locale);
}

/**
 * Percentage
 */
export function percentageFormatter(
	options?: Intl.NumberFormatOptions,
	locale?: string
) {
	return createFormatter({
		style: "percent",
		maximumFractionDigits: 1,
		minimumFractionDigits: 1,
		...options,
	}, locale);
}

/**
 * Money
 */
export function moneyFormatter(
	options?: Intl.NumberFormatOptions,
	locale?: string
) {
	return createFormatter({
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
		...options,
	}, locale);
}

/**
 * Money without fractions
 */
export function moneyNoFractionFormatter(
	options?: Intl.NumberFormatOptions,
	locale?: string
) {
	return createFormatter({
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 0,
		...options,
	}, locale);
}

/**
 * Hours
 */
export function hourFormatter(
	options?: Intl.NumberFormatOptions,
	locale?: string
) {
	return createFormatter({
		style: "unit",
		unit: "hour",
		unitDisplay: "narrow",
		maximumFractionDigits: 0,
		...options,
	}, locale);
}

/**
 * Minutes
 */
export function minuteFormatter(
	options?: Intl.NumberFormatOptions,
	locale?: string
) {
	return createFormatter({
		style: "unit",
		unit: "minute",
		unitDisplay: "narrow",
		maximumFractionDigits: 0,
		...options,
	}, locale);
}
